import { RankKit } from "@/components/site/RankKit";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import { CalcCard, CalcField, CalcRow, CalcStat, usd } from "@/components/site/CalcKit";
import { CalculatorScaffold } from "@/components/site/CalculatorScaffold";
import cashOutRefinanceHero from "@/assets/hero/hero-cash-out-refinance-two-women.jpg";
import { buildHead } from "@/lib/seo-head";
import { useShareableCalcState } from "@/lib/calc-share";
import { trackCalcEstimateViewed, trackCalcInput } from "@/lib/calculator-analytics";
import { calcCashOutRefi } from "@/lib/investor-calcs";

const FAQS = [
  {
    q: "How much cash can I take out of a rental property?",
    a: "Maximum loan is the program's cash-out LTV cap times the appraised value, less the balance being paid off and closing costs. Cash-out on business-purpose DSCR loans is commonly capped near 75% LTV, with lower caps for short-term rentals and foreign-national borrowers.",
  },
  {
    q: "Is cash-out refinance money taxable?",
    a: "Loan proceeds are generally not income, but tax treatment depends on your situation and on how the proceeds are used. Confirm with your CPA — we do not provide tax advice.",
  },
  {
    q: "What is the break-even on a cash-out refinance?",
    a: "Divide closing costs by the monthly benefit — the return you expect on redeployed proceeds, net of any increase in the payment. This tool shows that in months.",
  },
  {
    q: "Does a cash-out refinance hurt my DSCR?",
    a: "Usually yes, because the balance and the payment both rise while rent stays the same. The calculator shows DSCR before and after so you can see whether the new ratio still clears the program floor.",
  },
  {
    q: "Is there a seasoning requirement?",
    a: "Many programs require an ownership period before pricing cash-out off current appraised value rather than the purchase price plus documented improvements. Requirements vary by program.",
  },
  {
    q: "How does a lower appraised value affect my cash-out refinance proceeds?",
    a: "Because maximum loan is calculated as appraised value times the program's LTV cap, a lower-than-expected appraisal directly reduces both the maximum loan amount and the net cash you receive after payoff and closing costs. This is why many investors run this calculator at a few conservative value scenarios rather than only their expected appraisal.",
  },
  {
    q: "Can I do a cash-out refinance on a property I purchased with cash?",
    a: "Yes, this is often called a delayed-purchase or delayed-financing cash-out refinance, and it lets an investor recover capital tied up in an all-cash purchase. Many DSCR programs require a minimum ownership period before basing the new loan on appraised value rather than the original purchase price, so check the specific program's seasoning rule.",
  },
  {
    q: "What is the difference between rate-and-term and cash-out refinance for a rental?",
    a: "A rate-and-term refinance replaces the existing loan without taking meaningful cash out, generally used to change the rate or term, while a cash-out refinance pulls equity above the current payoff for reinvestment or other use. Cash-out refinances typically carry a lower maximum LTV than rate-and-term because the lender is extending more net new credit against the property.",
  },
  {
    q: "How much equity do I need before a cash-out refinance makes sense?",
    a: "You generally need enough equity so the resulting loan-to-value at the program's cash-out cap still leaves meaningful proceeds after payoff and closing costs, commonly meaning at least 25%–35% equity as a starting point. Below that, the transaction may generate too little net cash to justify the closing costs and rate reset.",
  },
  {
    q: "Will a cash-out refinance change my DSCR loan's prepayment penalty?",
    a: "Refinancing pays off the existing loan, which can trigger any remaining prepayment penalty on that loan, and the new loan will carry its own prepayment structure. Check the current note's penalty schedule and compare it against the new loan's terms before deciding on timing.",
  },
  {
    q: "How long does a cash-out refinance on an investment property typically take to close?",
    a: "Business-purpose DSCR cash-out refinances commonly take a few weeks from application to closing, driven mainly by appraisal scheduling and title work rather than income documentation. Timelines vary by property complexity, title issues, and appraiser availability in the local market.",
  },
  {
    q: "Does rental income get used to qualify for a DSCR cash-out refinance?",
    a: "Yes — DSCR loans qualify primarily off the subject property's rent relative to the new payment rather than the borrower's personal income, which is why the calculator shows before-and-after DSCR. Programs typically require the ratio to clear a minimum threshold, and figures shown here are illustrative estimates subject to credit review, appraisal, and full underwriting.",
  },
];

export const Route = createFileRoute("/cash-out-refinance-calculator")({
  head: () =>
    buildHead({
      title: "Cash-Out Refinance Calculator for Rentals | Simply Approved",
      description:
        "Estimate cash-out refinance proceeds on an investment property: maximum loan at your target LTV, closing costs, net cash, new payment, DSCR impact and break-even.",
      url: "/cash-out-refinance-calculator",
      keywords:
        "cash out refinance calculator, DSCR cash out refinance calculator, rental property cash out calculator, how much cash out of a rental, refinance proceeds calculator, cash out break even calculator, investment property refinance",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Cash-Out Refinance Calculator", url: "/cash-out-refinance-calculator" },
      ],
      service: { name: "Cash-Out Refinance Calculator" },
      faqs: FAQS,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Cash-Out Refinance Calculator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/cash-out-refinance-calculator",
          description: "Cash-out refinance proceeds, DSCR impact and break-even calculator for rentals.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: CashOutPage,
});

type Inputs = {
  value: number;
  balance: number;
  targetLtvPct: number;
  newRatePct: number;
  newTermYears: number;
  ioToggle: number;
  currentPitia: number;
  monthlyRent: number;
  otherMonthly: number;
  closingPct: number;
  closingFlat: number;
  reinvestPct: number;
};

const DEFAULT: Inputs = {
  value: 525000,
  balance: 280000,
  targetLtvPct: 75,
  newRatePct: 7.5,
  newTermYears: 30,
  ioToggle: 0,
  currentPitia: 2350,
  monthlyRent: 3400,
  otherMonthly: 620,
  closingPct: 2,
  closingFlat: 1995,
  reinvestPct: 8,
};

function CashOutPage() {
  const { state: i, patch, shareUrl } = useShareableCalcState<Inputs>("cash_out_refi", DEFAULT);
  const set = <K extends keyof Inputs>(k: K, v: Inputs[K]) => {
    patch(k, v);
    trackCalcInput("cash_out_refi", String(k), v as never);
  };

  const m = useMemo(
    () =>
      calcCashOutRefi({
        propertyValue: i.value,
        currentBalance: i.balance,
        targetLtvPct: i.targetLtvPct,
        newRatePct: i.newRatePct,
        newTermYears: i.newTermYears,
        interestOnly: i.ioToggle >= 1,
        currentPitia: i.currentPitia,
        currentPi: 0,
        monthlyRent: i.monthlyRent,
        otherMonthly: i.otherMonthly,
        closingCostPct: i.closingPct,
        closingCostFlat: i.closingFlat,
        reinvestReturnPct: i.reinvestPct,
      }),
    [i],
  );

  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    trackCalcEstimateViewed("cash_out_refi", { net_proceeds: Math.round(m.netProceeds) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aiFacts = [
    { label: "Property value", value: usd(i.value) },
    { label: "Current balance", value: usd(i.balance) },
    { label: `New loan at ${i.targetLtvPct}% LTV`, value: usd(m.newLoanAmount) },
    { label: "Net proceeds", value: usd(m.netProceeds) },
    { label: "New PITIA", value: usd(m.newPitia) },
    { label: "DSCR after refinance", value: (m.newDscr ?? 0).toFixed(2) },
    { label: "Break-even", value: m.breakEvenMonths ? `${m.breakEvenMonths} months` : "Not reached" },
  ];

  return (
    <CalculatorScaffold
      calculator="cash_out_refi"
      eyebrow="Cash-out refinance"
      title="Cash-Out Refinance Calculator for Investment Property"
      subtitle="See maximum proceeds at your target LTV, what lands in your account after costs, how the payment and DSCR change, and how long the costs take to earn back. Illustrative estimates only — not an approval, offer of credit, or commitment to lend."
      heroImage={{
        src: cashOutRefinanceHero,
        alt: "Two women reviewing rental-property equity and cash-out refinance plans for another investment purchase",
      }}
      afterHero={<RankKit page="cash-out-refinance-calculator" />}
      shareUrl={shareUrl}
      shareTitle="My cash-out refinance scenario"
      inputs={
        <>
          <CalcCard title="1. Property & existing loan">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Property value" value={i.value} onChange={(v) => set("value", v)} prefix="$" hint="Appraised value governs — this is an assumption until an appraisal is completed." />
              <CalcField label="Current loan balance" value={i.balance} onChange={(v) => set("balance", v)} prefix="$" />
              <CalcField label="Current monthly PITIA" value={i.currentPitia} onChange={(v) => set("currentPitia", v)} prefix="$" />
              <CalcField label="Monthly rent" value={i.monthlyRent} onChange={(v) => set("monthlyRent", v)} prefix="$" />
            </div>
          </CalcCard>
          <CalcCard title="2. New loan">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Target cash-out LTV" value={i.targetLtvPct} onChange={(v) => set("targetLtvPct", v)} suffix="%" step={5} hint="Program caps vary; DSCR cash-out is commonly capped near 75%." />
              <CalcField label="New note rate" value={i.newRatePct} onChange={(v) => set("newRatePct", v)} suffix="%" step={0.125} />
              <CalcField label="Amortization (years)" value={i.newTermYears} onChange={(v) => set("newTermYears", v)} step={5} />
              <CalcField label="Interest-only? (1 = yes)" value={i.ioToggle} onChange={(v) => set("ioToggle", v)} step={1} />
              <CalcField label="Taxes + insurance + HOA (monthly)" value={i.otherMonthly} onChange={(v) => set("otherMonthly", v)} prefix="$" />
            </div>
          </CalcCard>
          <CalcCard title="3. Costs & redeployment">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Closing costs (% of loan)" value={i.closingPct} onChange={(v) => set("closingPct", v)} suffix="%" step={0.25} />
              <CalcField label="Additional flat costs" value={i.closingFlat} onChange={(v) => set("closingFlat", v)} prefix="$" hint="Include any prepayment penalty on the loan being retired." />
              <CalcField label="Expected return on redeployed cash" value={i.reinvestPct} onChange={(v) => set("reinvestPct", v)} suffix="%" step={1} hint="Your own assumption — not a projection by us." />
            </div>
          </CalcCard>
        </>
      }
      results={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <CalcStat label="Net proceeds" value={usd(m.netProceeds)} colorClass={m.netProceeds > 0 ? "text-emerald-600" : "text-destructive"} />
            <CalcStat label="New loan amount" value={usd(m.newLoanAmount)} />
            <CalcStat label="DSCR after" value={(m.newDscr ?? 0).toFixed(2)} colorClass={(m.newDscr ?? 0) >= 1.25 ? "text-emerald-600" : (m.newDscr ?? 0) >= 1 ? "text-amber-600" : "text-destructive"} />
            <CalcStat label="Break-even" value={m.breakEvenMonths ? `${m.breakEvenMonths} mo` : "—"} />
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <h3 className="font-display text-base font-semibold text-foreground">Proceeds & payment detail</h3>
            <dl className="mt-3 space-y-1.5 text-sm">
              <CalcRow label={`Max loan at ${i.targetLtvPct}% LTV`} value={usd(m.maxLoan)} />
              <CalcRow label="Less payoff of current balance" value={`−${usd(i.balance)}`} />
              <CalcRow label="Gross proceeds" value={usd(m.grossProceeds)} strong />
              <CalcRow label="Less closing costs" value={`−${usd(m.closingCosts)}`} />
              <CalcRow label="Net proceeds" value={usd(m.netProceeds)} strong colorClass={m.netProceeds > 0 ? "text-emerald-600" : "text-destructive"} />
              <CalcRow label="New principal & interest" value={usd(m.newPi)} />
              <CalcRow label="New PITIA" value={usd(m.newPitia)} strong />
              <CalcRow label="Payment change" value={`${m.paymentChange >= 0 ? "+" : "−"}${usd(Math.abs(m.paymentChange))}`} colorClass={m.paymentChange <= 0 ? "text-emerald-600" : "text-destructive"} />
              <CalcRow label="DSCR before" value={(m.currentDscr ?? 0).toFixed(2)} />
              <CalcRow label="DSCR after" value={(m.newDscr ?? 0).toFixed(2)} />
              <CalcRow label="Assumed monthly income on proceeds" value={usd(m.reinvestMonthlyIncome)} />
            </dl>
          </div>
        </div>
      }
      aiFacts={aiFacts}
      aiSuggestions={[
        "Does this cash-out still clear a 1.25 DSCR?",
        "Second lien or full cash-out refinance — which fits here?",
        "What drives my break-even period?",
        "What could reduce my proceeds at closing?",
      ]}
      methodologyIntro="Proceeds are capped by leverage; the decision is whether the redeployed cash outruns the higher payment."
      methodology={
        <>
          <p>
            <strong>Proceeds.</strong> Target LTV × value, less the payoff, less closing costs. Any prepayment penalty
            on the loan being retired belongs in the flat-cost field.
          </p>
          <p>
            <strong>DSCR impact.</strong> Rent stays flat while the payment rises, so the ratio falls. If the new DSCR
            drops below the program floor, the proceeds shown are not achievable at that leverage.
          </p>
          <p>
            <strong>Break-even.</strong> Closing costs ÷ (assumed monthly income on the proceeds − the payment
            increase). It uses your own return assumption and ignores taxes, vacancy on the next property, and the time
            value of money. A second lien can beat a full refinance when the existing first carries a below-market rate
            — compare it with the equity tools.
          </p>
        </>
      }
      faqs={FAQS}
      faqTitle="Cash-out refinance FAQ"
      related={[
        { to: "/dscr-cash-out-refinance", label: "DSCR cash-out refinance program" },
        { to: "/ltv-equity-calculator", label: "LTV & equity calculator" },
        { to: "/dscr-second-lien-heloc", label: "Second-lien DSCR HELOC" },
        { to: "/dscr-heloc-rates", label: "DSCR HELOC pricing" },
        { to: "/brrrr-calculator", label: "BRRRR calculator" },
        { to: "/prepayment-penalty-calculator", label: "Prepayment penalty calculator" },
      ]}
      ctaTitle="Put the equity to work"
      ctaBody="Send the property, balance, and rent and a licensed loan officer will outline realistic proceeds and structures for business-purpose investment property."
    />
  );
}
